import { buildProductPhotographyGuide } from './product-photography-guide';
import { buildProductIdeasGuide } from './product-ideas-guide';
import { withReferenceStyleWorkflow } from './reference-style-guide';
import { guideVisual, type SeoGuideVisual } from './guide-visuals';

export type SeoGuideSlug =
  | 'product-photography-ideas'
  | 'ai-fashion-model-generator-ecommerce'
  | 'ai-product-photography-workflow'
  | 'reference-image-ai-generation-workflow'
  | 'ai-lookbook-production-guide'
  | 'image-to-video-fashion-ads';

export type SeoGuideLocale = 'zh' | 'en';

export const SEO_GUIDE_AUTHOR = 'DOBIDOBI Editorial Team';
export const SEO_GUIDE_PUBLISHED_DATE = '2026-06-19';
export const SEO_GUIDE_MODIFIED_DATE = '2026-08-15';

export interface SeoGuideSource {
  label: string;
  href: string;
}

export interface SeoGuideHeroSlide {
  src: string;
  alt: string;
  width: number;
  height: number;
}

export interface SeoGuideSection {
  id: string;
  title: string;
  paragraphs: string[];
  bullets?: string[];
  table?: SeoGuideTable;
  visuals?: SeoGuideVisual[];
  example?: { label: string; text: string };
  links?: Array<{ label: string; href: string }>;
}

export interface SeoGuideTable {
  title: string;
  headers: [string, string, string];
  rows: [string, string, string][];
}

export interface SeoGuideFaq {
  question: string;
  answer: string;
}

export interface SeoGuideArticle {
  slug: SeoGuideSlug;
  path: string;
  priority: number;
  landingPath: string;
  title: string;
  metadataTitle?: string;
  description: string;
  primaryKeyword: string;
  searchIntent: string;
  readingTime: string;
  heroImage: string;
  heroImageAlt: string;
  heroImageWidth?: number;
  heroImageHeight?: number;
  images?: string[];
  publishedDate?: string;
  modifiedDate?: string;
  startHref?: string;
  heroSlides?: SeoGuideHeroSlide[];
  takeawaysTitle: string;
  takeaways: string[];
  sections: SeoGuideSection[];
  commonMistakesTitle?: string;
  commonMistakes?: string[];
  table: SeoGuideTable;
  checklistTitle: string;
  checklist: string[];
  sourceTitle: string;
  sourceEyebrow?: string;
  sources: SeoGuideSource[];
  faq: SeoGuideFaq[];
  ctaTitle: string;
  ctaBody: string;
  ctaLabel: string;
}

export const SEO_GUIDE_CONFIGS: Array<Pick<SeoGuideArticle, 'slug' | 'path' | 'priority' | 'landingPath' | 'heroImage'>> = [
  {
    slug: 'product-photography-ideas',
    path: '/guides/product-photography-ideas',
    priority: 0.73,
    landingPath: '/ai-clothing-photography',
    heroImage: buildProductIdeasGuide('en').heroImage,
  },
  {
    slug: 'ai-fashion-model-generator-ecommerce',
    path: '/guides/ai-fashion-model-generator-ecommerce',
    priority: 0.73,
    landingPath: '/ai-fashion-model-generator',
    heroImage: '/landing/seo-cases/model-library-product-angle.webp',
  },
  {
    slug: 'ai-product-photography-workflow',
    path: '/guides/ai-product-photography-workflow',
    priority: 0.73,
    landingPath: '/ai-product-photography',
    heroImage: buildProductPhotographyGuide('en').heroImage,
  },
  {
    slug: 'reference-image-ai-generation-workflow',
    path: '/guides/reference-image-ai-generation-workflow',
    priority: 0.72,
    landingPath: '/reference-image-ai-generator',
    heroImage: '/guides/reference-image/comparison-3.webp',
  },
  {
    slug: 'ai-lookbook-production-guide',
    path: '/guides/ai-lookbook-production-guide',
    priority: 0.72,
    landingPath: '/ai-lookbook-generator',
    heroImage: '/guides/ai-lookbook/hero-cover.png',
  },
  {
    slug: 'image-to-video-fashion-ads',
    path: '/guides/image-to-video-fashion-ads',
    priority: 0.71,
    landingPath: '/image-to-video-fashion',
    heroImage: '/landing/seo-cases/scene-library-fashion-editorial.webp',
  },
];

const sourceLinks = {
  seoStarter: {
    zh: 'Google 搜索中心：SEO 入门指南',
    en: 'Google Search Central: SEO Starter Guide',
    href: 'https://developers.google.com/search/docs/fundamentals/seo-starter-guide',
  },
  articleSchema: {
    zh: 'Google 搜索中心：Article 结构化数据',
    en: 'Google Search Central: Article structured data',
    href: 'https://developers.google.com/search/docs/appearance/structured-data/article',
  },
  imageLink: {
    zh: 'Google Merchant Center：image_link 商品图片规范',
    en: 'Google Merchant Center: image_link product image specification',
    href: 'https://support.google.com/merchants/answer/6324350?hl=en',
  },
  additionalImage: {
    zh: 'Google Merchant Center：additional_image_link 规范',
    en: 'Google Merchant Center: additional_image_link specification',
    href: 'https://support.google.com/merchants/answer/6324370?hl=en',
  },
  lifestyleImage: {
    zh: 'Google Merchant Center：lifestyle_image_link 规范',
    en: 'Google Merchant Center: lifestyle_image_link specification',
    href: 'https://support.google.com/merchants/answer/9103186?hl=en',
  },
  adsImage: {
    zh: 'Google Ads：图片素材规范',
    en: 'Google Ads: image asset specifications',
    href: 'https://support.google.com/google-ads/answer/9566341?hl=en',
  },
};

function sources(locale: SeoGuideLocale, keys: Array<keyof typeof sourceLinks>): SeoGuideSource[] {
  return keys.map((key) => ({
    label: sourceLinks[key][locale],
    href: sourceLinks[key].href,
  }));
}

const zhGuides: Record<SeoGuideSlug, SeoGuideArticle> = {
  'product-photography-ideas': buildProductIdeasGuide('zh'),
  'ai-fashion-model-generator-ecommerce': {
    slug: 'ai-fashion-model-generator-ecommerce',
    path: '/guides/ai-fashion-model-generator-ecommerce',
    priority: 0.73,
    landingPath: '/ai-fashion-model-generator',
    title: '如何使用 AI 时尚模特生成器完成电商上新',
    metadataTitle: 'AI 时尚模特生成器电商指南 | DOBIDOBI',
    description: '把平铺图或服装商品图转换成一致的模特展示图，用于商品页、Campaign 和社媒内容。',
    primaryKeyword: 'AI 模特图',
    searchIntent: '商业型 / 交易前评估',
    readingTime: '约 8 分钟',
    heroImage: '/landing/seo-cases/model-library-product-angle.webp',
    heroImageAlt: 'AI 模特图生成：模特库人物结合商品图片生成不同角度的电商展示图',
    heroSlides: [
      { src: '/guides/ai-fashion-model/hero-slides/01-satin-dress.webp', alt: '穿着香槟色缎面礼服的女性模特全身展示图', width: 2048, height: 2731 },
      { src: '/guides/ai-fashion-model/hero-slides/02-black-tailoring.jpeg', alt: '穿着黑色西装外套和亮片上衣的女性模特展示图', width: 896, height: 1200 },
      { src: '/guides/ai-fashion-model/hero-slides/03-beige-jacket.jpeg', alt: '穿着米色短夹克、蓝色针织衫和短裤的女性模特展示图', width: 896, height: 1200 },
      { src: '/guides/ai-fashion-model/hero-slides/04-black-bag-look.webp', alt: '穿着黑色上衣和白色短裙并手提黑色手袋的女性模特展示图', width: 2048, height: 2743 },
      { src: '/guides/ai-fashion-model/hero-slides/05-black-romper.webp', alt: '穿着黑色短袖连体衣和高跟鞋的女性模特展示图', width: 1024, height: 1536 },
      { src: '/guides/ai-fashion-model/hero-slides/06-white-jumpsuit.png', alt: '穿着白色无袖连体裤的女性模特室内展示图', width: 1024, height: 1376 },
    ],
    takeawaysTitle: '核心要点',
    takeaways: [
      '从清晰的服装图片开始，并先定义哪些商品细节绝对不能改变。',
      '生成前确定模特、姿势、场景和最终使用渠道。',
      '先审核服装准确性，再判断整体审美。',
      '从一个已确认方向扩展协调一致的上新素材组。',
      '把 AI 输出当作经过审核的生产素材，而不是自动生成的最终图片。',
    ],
    sections: [
      {
        id: 'intent',
        title: 'AI 时尚模特生成器应该帮助你完成什么',
        paragraphs: [
          'AI 时尚模特生成器把真实服装或商品放到生成模特身上，不需要为每个概念都重新拍摄。最有价值的任务不是“生成一个模特”，而是“把一件已确认服装变成一组服务于具体电商和 Campaign 需求的协调模特图”。',
          '这个区别很重要。即使画面视觉上很吸引人，只要服装颜色漂移、领口变化、Logo 消失或姿势遮住商品，就仍然无法使用。生成前先定义业务目标，以及必须保持准确的商品细节。',
        ],
      },
      {
        id: 'materials',
        title: 'Step 1：准备服装图片',
        paragraphs: [
          '使用光线均匀、完整显示服装的清晰图片。避免强阴影、遮挡结构的褶皱、覆盖商品的手部和低分辨率裁切。对于图案、Logo、五金和特殊缝线，在流程允许时补充局部参考图。',
          '生成前记录不可改变的细节：颜色、图案位置、长度、廓形、领口、袖子、闭合方式和品牌标识。这份记录就是每张结果的审核清单。',
        ],
        visuals: [
          guideVisual('fashionModelGarmentSource', '用于 AI 模特图生成的米色风衣商品正面图', 'Step 1：使用无遮挡、结构清晰的商品图锁定服装真值。'),
        ],
      },
      {
        id: 'workflow',
        title: 'Step 2：定义模特与上新场景',
        paragraphs: [
          '根据目标顾客和 Campaign 选择模特，而不是只看是否时髦。考虑年龄范围、呈现方式、体型、姿势，以及整组素材需要的多样性。',
          '随后明确图片会用在哪里。商品详情页需要清楚展示合身度和廓形；付费社媒图需要更强情绪和文案留白；Lookbook 则需要多个商品之间保持稳定的视觉节奏。',
        ],
        visuals: [
          guideVisual('fashionModelReference', 'AI 模特图工作流使用的女性模特参考图', 'Step 2：选择一张模特与姿势参考，固定人物气质、构图和光线方向。'),
        ],
      },
      {
        id: 'qa',
        title: 'Step 3：先生成方向，再批量生产',
        paragraphs: [
          '先生成一小组方向，每次只改变一个决定，例如姿势或背景。在服装、模特和视觉方向确认可以共同工作前，不要批量生成几十张最终素材。',
          '根据商品准确性、品牌匹配度和渠道用途选择一个方向。方向通过后，再扩展所需角度、画幅和格式。',
        ],
        visuals: [
          guideVisual('fashionModelGenerationInputs', 'DOBIDOBI 编辑器中组合模特参考与风衣商品图的生成输入', 'Step 3：在 DOBIDOBI 中组合人物参考和服装商品图，再生成首轮方向。'),
        ],
      },
      {
        id: 'conversion',
        title: 'Step 4：先审核服装，再看模特',
        paragraphs: [
          '按固定顺序检查商品；任何会影响消费者理解合身度、结构或包含内容的实质性错误，都必须经过人工复核。如果关键商品细节错误，只能把图片当作概念图，不能作为可发布电商素材。',
        ],
        bullets: [
          '颜色和图案。',
          '廓形和比例。',
          '领口、袖子、缝线和闭合方式。',
          'Logo、文字、五金和配饰。',
          '服装与身体之间自然的合身关系。',
          '手部、面部、人体结构和背景伪影。',
        ],
        visuals: [
          guideVisual('fashionModelReviewResults', 'DOBIDOBI 结果审核界面检查风衣颜色、比例和细节', 'Step 4：先核对服装颜色、结构与细节，再评估模特和整体氛围。'),
        ],
      },
      {
        id: 'dobi',
        title: 'Step 5：建立协调一致的上新素材组',
        paragraphs: [
          '固定服装源图和已选择的视觉参考，再根据各渠道调整构图。这样既能保持连续性，也不会强迫同一裁切承担所有用途。',
        ],
        bullets: [
          '清晰的正面模特展示图。',
          '在商品需要时提供侧面或背面图。',
          '展示面料或结构的细节裁切。',
          '用于社媒内容的生活方式画面。',
          '为文案预留干净空间的广告版本。',
          '可以继续进入短视频的已确认静帧。',
        ],
        visuals: [
          guideVisual('fashionModelApprovedLaunchImage', '通过审核的米色风衣 AI 电商模特全身图', 'Step 5：将审核通过的画面纳入统一的电商上新素材组。'),
        ],
      },
    ],
    commonMistakesTitle: '需要避免的常见错误',
    commonMistakes: [
      '没有先定义用途就开始生成。商品详情页、Lookbook、广告和社媒需要不同的画面决策。',
      '先认可氛围，再检查服装。吸引人的光线无法弥补被改变的商品。',
      '一次改变太多变量，导致团队无法判断是模特、姿势、背景还是参考图带来了改善。',
      '未经披露或审核就发布。团队应遵守目标市场、平台和合成媒体适用的政策与标识要求。',
    ],
    table: {
      title: 'AI 模特图生成在电商流程里的角色',
      headers: ['阶段', '适合用 AI 做什么', '人工必须检查什么'],
      rows: [
        ['设计评审', '快速预览上身比例、搭配和模特气质', '版型是否被改写，关键结构是否准确'],
        ['上新预热', '生成社媒封面、海报草案、种草图', '品牌调性、商标、人物姿态和使用授权'],
        ['商品详情页', '补充角度图、生活方式图、搭配图', '颜色、材质、尺寸和平台图片规范'],
        ['广告测试', '扩展不同场景、比例和情绪版本', '文字覆盖、安全区域、素材真实性说明'],
      ],
    },
    checklistTitle: 'AI 时尚模特图检查清单',
    checklist: [
      '服装与源图一致。',
      '模特与姿势适合目标顾客和渠道。',
      '商品保持清晰可见，并且在商业上容易理解。',
      '手部、人体结构、文字、Logo 和背景已经审核。',
      '每个输出都有明确投放位置和画幅。',
      '最终文件使用描述性文件名和 alt 文本。',
    ],
    sourceTitle: '延伸参考',
    sources: sources('zh', ['imageLink', 'additionalImage', 'lifestyleImage', 'adsImage']),
    faq: [
      {
        question: 'AI 时尚模特可以替代传统拍摄吗？',
        answer: '它可以降低早期方向测试和素材扩展所需的成本与时间；但如果任务要求精确合身度、运动、纹理、模特授权或高风险 Campaign 制作，传统摄影仍可能更合适。',
      },
      {
        question: '平铺图可以转换成模特上身图吗？',
        answer: '可以。只要服装廓形和细节清楚，平铺图就是很好的起点；复杂垂坠、透明材质和被遮住的结构需要更严格复核。',
      },
      {
        question: '如何让多个商品保持同一风格？',
        answer: '先确认一个模特和视觉方向，再复用同一参考、光线逻辑、背景与裁切系统；只更换商品，保持 Campaign 规则稳定。',
      },
    ],
    ctaTitle: '创建第一组模特上新素材',
    ctaBody: '从一张清晰服装图开始，确认方向，再扩展成上新真正需要的角度和格式。',
    ctaLabel: '进入 AI 模特图生成',
  },
  'ai-product-photography-workflow': buildProductPhotographyGuide('zh'),
  'reference-image-ai-generation-workflow': {
    slug: 'reference-image-ai-generation-workflow',
    path: '/guides/reference-image-ai-generation-workflow',
    priority: 0.72,
    landingPath: '/reference-image-ai-generator',
    title: '参考图 AI 生成工作流',
    description: '使用视觉参考控制构图、光线、情绪和 Campaign 一致性，同时避免复制其他品牌作品。',
    primaryKeyword: '参考图生成',
    searchIntent: '信息型 / 商业型',
    readingTime: '约 8 分钟',
    heroImage: '/guides/reference-image/comparison-3.webp',
    heroImageAlt: '参考图生成图片：米色度假风参考图用于延展多角度 AI Lookbook',
    takeawaysTitle: '核心要点',
    takeaways: [
      '把商品真值和视觉方向分开。',
      '描述希望从参考图借用的视觉原则，而不只是描述图片里有什么。',
      '先锁定一个方向，再生成完整素材组。',
      '审核品牌匹配、商品准确性和不必要的模仿。',
      '把已确认参考保存为可复用的视觉系统。',
    ],
    sections: [
      {
        id: 'why-reference',
        title: '参考图为什么有用',
        paragraphs: [
          '文字提示可以描述情绪，但视觉参考能传达难以精确写出的关系：镜头距离、光线柔和度、背景密度、色彩平衡、负空间和造型节奏。',
          '参考图应该指导视觉系统，而不是替代商品源图。用清晰商品图保护商品本身，再用独立参考塑造场景；这种分离让流程更容易审核和重复。',
        ],
      },
      {
        id: 'select',
        title: 'Step 1：明确每个输入的任务',
        paragraphs: [
          '不是每个项目都需要四张图片，但每个输入都应该只有一个明确任务。当一张图片被要求同时控制所有内容时，输出最容易漂移。',
        ],
        bullets: [
          '商品源图：定义必须保持准确的商品。',
          '场景参考：指导环境、构图、镜头角度和光线。',
          '品牌参考：指导色板、视觉克制、造型与整体语气。',
          '渠道参考：说明最终投放位置需要的裁切、安全区或版式。',
        ],
        visuals: [
          guideVisual('referenceProductSource', '参考图生成工作流使用的棕色夹克商品源图', 'Step 1：商品源图负责锁定夹克的颜色、结构和材质。'),
          guideVisual('referenceSceneReference', '参考图生成工作流使用的米色中性棚拍场景参考', 'Step 1：场景参考只负责背景、光线和空间氛围。'),
          guideVisual('referenceBrandChannelReference', '参考图生成工作流使用的深色长外套品牌构图参考', 'Step 1：品牌与渠道参考负责构图节奏和画面气质。'),
        ],
      },
      {
        id: 'decompose',
        title: 'Step 2：把参考图翻译成视觉决策',
        paragraphs: [
          '生成前先写出参考图中可以迁移的视觉属性。这个过程能帮助团队对齐方向并减少无意模仿；目标是在品牌自己的系统中重新建立视觉原则，而不是复制某一张具体图片。',
        ],
        bullets: [
          '构图：居中、不对称、近景裁切或全身。',
          '镜头：平视、低角度、俯拍或微距。',
          '光线：柔和日光、硬闪、轮廓光或棚拍渐变。',
          '色板：暖中性、高对比、低饱和或单色。',
          '背景：极简、Editorial、环境化或纹理背景。',
          '能量：安静、玩味、高级、技术感或怀旧。',
          '负空间：为文案或界面元素预留的位置。',
        ],
        visuals: [
          guideVisual('referenceImageDecompositionInterface', 'DOBIDOBI 将参考图拆解为构图、镜头、光线、色板和背景等视觉变量的界面', 'Step 2：把参考图拆解为可执行的构图、镜头、光线、色板、背景和负空间决策。'),
        ],
      },
      {
        id: 'series',
        title: 'Step 3：一次只测试一个变量',
        paragraphs: [
          '生成一小组对比图。固定商品，只测试两到三个场景或光线选项；选出最强方向后，保持参考稳定，再扩展到新商品、角度或格式。',
          '如果每次生成同时改变商品、模特、场景、光线和裁切，团队就无法判断究竟哪个决定改善了结果。',
        ],
        visuals: [
          guideVisual('referenceComparison1', '同一棕色夹克在受控参考方向下生成的第一组对比结果', 'Step 3：固定商品与主参考，只改变一个视觉变量。'),
          guideVisual('referenceComparison2', '同一棕色夹克在受控参考方向下生成的第二组对比结果', 'Step 3：对比姿势变化时，继续检查夹克结构与系列一致性。'),
          guideVisual('referenceComparison3', '同一棕色夹克在受控参考方向下生成的第三组对比结果', 'Step 3：对比构图变化时，保持商品颜色和材质稳定。'),
          guideVisual('referenceComparison4', '同一棕色夹克在受控参考方向下生成的第四组对比结果', 'Step 3：把可复用的方向保留为同一系列，而不是重新随机生成。'),
        ],
      },
      {
        id: 'review-reference',
        title: 'Step 4：审核参考图对结果的影响',
        paragraphs: [
          '拒绝复现不应被带入结果的独特 Logo、文字、可识别角色或高度特定创意元素。团队应使用自有、已授权或被允许使用的参考图，并在商业发布前审核生成素材。',
        ],
        bullets: [
          '商品是否仍然准确？',
          '结果是否体现了预期视觉属性？',
          '它是否像自己的品牌，而不是源图的复制品？',
          '这个方向能否扩展到完整 Campaign 素材组？',
        ],
        visuals: [
          guideVisual('referenceReviewInterface', 'DOBIDOBI 结果审核界面用于检查参考图对生成结果的影响', 'Step 4：在结果界面逐项检查参考图是否影响了不应改变的商品信息。'),
        ],
      },
      {
        id: 'save-system',
        title: 'Step 5：保存可复用的视觉系统',
        paragraphs: [
          '把参考图与简短方向说明一起保存，记录已确认的色板、光线、镜头逻辑、背景密度和渠道裁切。下一次生成商品或 Campaign 素材时继续复用这套组合。',
          '一套可复用方向可以支持商品摄影、时尚模特图、Lookbook、广告变化和图生视频的已确认静帧。价值来自整个素材组的一致性，而不是一张意外惊艳的图片。',
        ],
        visuals: [
          guideVisual('referenceComparison1', '审核通过的棕色夹克参考图生成结果', 'Step 5：保存审核通过的输出、输入和审核备注，形成可复用的视觉系统。'),
        ],
      },
    ],
    commonMistakesTitle: '需要避免的常见错误',
    commonMistakes: [
      '把受版权保护的 Campaign 当成复制指令。应提取通用视觉属性，形成原创品牌表达。',
      '组合相互冲突的参考图。极简棚拍和拥挤街头 Editorial 可能把结果拉向相反方向。',
      '依赖模糊标签。“高级”或“电影感”对不同人含义不同，应描述光线、色板、构图和质感。',
      '每生成一张就更换参考图。应先确认方向，再持续复用足够长时间以形成一致系列。',
    ],
    table: {
      title: '参考图在 AI 工作流里的分工',
      headers: ['参考图类型', '它负责什么', '不要让它负责什么'],
      rows: [
        ['主风格参考', '光线、颜色、构图、整体情绪', '商品结构和具体商业信息'],
        ['商品参考', '款式、材质、比例、局部细节', '场景氛围和人物气质'],
        ['渠道参考', '画幅、留白、版式和安全区', '审美方向和商品准确性'],
        ['案例参考', '团队沟通和结果对比', '照抄第三方创意或人物形象'],
      ],
    },
    checklistTitle: '参考图工作流检查清单',
    checklist: [
      '每个输入都有一个明确角色。',
      '商品源图清晰且准确。',
      '参考图已经被翻译成具体视觉决策。',
      '输出具有原创性，并适合进入商业审核。',
      '已确认方向可以跨商品和渠道扩展。',
      '商品细节、文字、Logo、手部和背景已经检查。',
    ],
    sourceTitle: '延伸参考',
    sources: sources('zh', ['seoStarter', 'articleSchema', 'adsImage']),
    faq: [
      {
        question: '什么样的参考图才算好？',
        answer: '有用的参考图能清楚传达要控制的视觉质量，例如构图、光线、色板或场景；同时应适合最终渠道，并在法律与授权范围内可用。',
      },
      {
        question: '可以使用多张参考图吗？',
        answer: '可以，但每张参考图都应有不同任务。避免同时使用给出冲突方向的图片。',
      },
      {
        question: '参考图如何提高一致性？',
        answer: '它们为每次生成提供同一视觉锚点。配合固定商品源图和书面方向，可以减少整组 Campaign 中的随机变化。',
      },
    ],
    ctaTitle: '构建可重复使用的视觉方向',
    ctaBody: '从清晰商品图开始，定义参考负责什么，确认一个方向，并在完整素材组中复用。',
    ctaLabel: '进入参考图生成图片',
  },
  'ai-lookbook-production-guide': {
    slug: 'ai-lookbook-production-guide',
    path: '/guides/ai-lookbook-production-guide',
    priority: 0.72,
    landingPath: '/ai-lookbook-generator',
    title: 'AI Lookbook 制作指南',
    description: '一套把系列 Brief、商品图和视觉参考转成连贯数字 Lookbook 的实用工作流。',
    primaryKeyword: 'AI Lookbook',
    searchIntent: '商业型 / 内容生产',
    readingTime: '约 9 分钟',
    heroImage: '/guides/ai-lookbook/hero-cover.png',
    heroImageAlt: 'AI Lookbook 生成：黑白服装设计草图延展为整组模特系列图',
    takeawaysTitle: '核心要点',
    takeaways: [
      '生成单张 Look 之前，先规划整个系列故事。',
      '定义可重复的 Shot List 和视觉系统。',
      '保持模特气质、光线、背景和裁切节奏一致。',
      '审核整个序列中的商品准确性和连续性。',
      '围绕读者的决策路径设计 Lookbook。',
    ],
    sections: [
      {
        id: 'series',
        title: '什么让 Lookbook 像一个完整系列',
        paragraphs: [
          'Lookbook 不只是一个装着好看时尚图片的文件夹。它是一个序列：介绍系列、建立视觉语言、展示最重要的商品，并提供足够细节帮助读者理解每件单品。',
          'AI 可以帮助测试方向并生成变体，但整个序列仍需编辑规划。没有系列 Brief、可重复的模特逻辑和 Shot List，结果通常像事后拼接的无关图片。',
        ],
      },
      {
        id: 'inputs',
        title: 'Step 1：写系列 Brief',
        paragraphs: [
          '用一小段话定义受众、季节、商品重点和视觉氛围，然后列出必须保持一致的内容。这份 Brief 将成为选择参考和审核输出的标准。',
        ],
        bullets: [
          '模特或模特家族。',
          '光线和色板。',
          '背景密度。',
          '镜头距离和角度。',
          '图片比例和裁切节奏。',
          '造型规则。',
          '不能改变的商品细节。',
        ],
        visuals: [
          guideVisual('lookbookCollectionBrief', 'AI Lookbook 系列 Brief 与视觉情绪板示例', 'Step 1：用季节、受众、色彩、场景和禁用元素写清系列 Brief。'),
        ],
      },
      {
        id: 'angles',
        title: 'Step 2：规划页面顺序',
        paragraphs: [
          '一本简单的数字 Lookbook 可以按开场、主打造型、辅助造型、多角度与细节、收尾的结构展开。先规划顺序再批量生成，可以避免无用图片，也让整组内容更容易审核。',
        ],
        bullets: [
          '系列开场：建立季节、受众和氛围。',
          '主打造型：优先展示最强廓形或最重要商品。',
          '辅助造型：扩展系列，但不重复同一姿势或裁切。',
          '多角度和细节图：解释结构、面料、搭配和版型。',
          '收尾画面：强化系列识别，并引向下一个行动。',
        ],
        visuals: [
          guideVisual('lookbookSequencePlanning', 'DOBIDOBI 中规划 AI Lookbook 页面顺序与画面节奏', 'Step 2：先规划开场、重点款、细节、搭配和收尾的页面顺序。'),
        ],
      },
      {
        id: 'shot-list',
        title: 'Step 3：建立 Shot List',
        paragraphs: [
          '为每个重点商品决定是否需要正面、侧面、背面、近景细节、坐姿、运动或环境画面；不是每个商品都需要每个角度。',
          '对同类商品保持 Shot 逻辑一致。例如每件夹克都有正面主图和细节裁切，读者会更容易比较；更实验性的场景则留给系列开场与 Campaign 画面。',
        ],
        visuals: [
          guideVisual('lookbookShotList', 'AI Lookbook 正面、侧面、背面和细节角度 Shot List', 'Step 3：建立覆盖正面、侧面、背面与细节的 Shot List。'),
        ],
      },
      {
        id: 'visual-direction',
        title: 'Step 4：锁定统一视觉方向',
        paragraphs: [
          '完整制作前先选定一个已批准的参考方向，保持光线、色板、模特气质和背景规则稳定。通过姿势、造型和页面角色引入变化，不要随机改变整个视觉系统。',
          '使用不同模特时，仍可以通过光线、构图、造型和色彩处理保持连续性。只要编辑规则清晰，多样性与一致性可以并存。',
        ],
        visuals: [
          guideVisual('lookbookVisualDirection', 'AI Lookbook 锁定后的系列视觉方向与情绪板', 'Step 4：锁定统一的光线、色彩、场景和模特气质。'),
        ],
      },
      {
        id: 'review-sequence',
        title: 'Step 5：审核整组顺序与一致性',
        paragraphs: [
          '先逐张检查商品的颜色、廓形、图案、Logo、闭合件和结构，再把 Lookbook 作为一个整体审核。即使单张图很强，如果破坏了系列节奏也应删除。',
        ],
        bullets: [
          '开场是否建立了清晰方向？',
          '相邻页面是否有意识地互相关联？',
          '姿势和裁切是否在不失一致性的前提下有变化？',
          '主打商品是否容易识别？',
          '细节图是否增加了有用信息？',
          '最终顺序是否自然引向商品或 Campaign CTA？',
        ],
        visuals: [
          guideVisual('lookbookSequenceReview', 'DOBIDOBI 中逐张审核 AI Lookbook 系列顺序和一致性', 'Step 5：在同一序列中检查人物、光线、服装颜色和裁切节奏。'),
        ],
      },
      {
        id: 'prepare-publishing',
        title: 'Step 6：准备数字发布素材',
        paragraphs: [
          '在保留目标展示所需分辨率的同时优化图片加载。使用能解释商品和场景的描述性文件名和 alt 文本，并在格式允许时链接到商品或相关系列页。',
          '如果 Lookbook 作为网页发布，还应有清晰标题、简短介绍、商品路径和下一步行动。Lookbook 不仅用于激发灵感，还应帮助读者继续发现或购买。',
        ],
        visuals: [
          guideVisual('lookbookDigitalExperience', '审核通过的 AI Lookbook 全身造型图用于数字发布', 'Step 6：将审核通过的画面按页面、社媒和广告用途完成数字发布准备。'),
        ],
      },
    ],
    commonMistakesTitle: '需要避免的常见错误',
    commonMistakes: [
      '每套 Look 都从新 prompt 开始，最终得到拼贴而不是系列。',
      '重复同一种姿势与裁切，把一致性误解成完全相同。',
      '为了氛围遮住商品细节，让服装在商业上难以理解。',
      '跳过页面顺序审核，忽略相邻画面的节奏。',
    ],
    table: {
      title: 'AI Lookbook 的页面结构建议',
      headers: ['模块', '内容重点', 'SEO 作用'],
      rows: [
        ['系列开场', '一句话说明主题、季节和人群', '让 H1 和首段对齐主关键词'],
        ['关键款式', '每件重点款的廓形、材质、搭配', '覆盖长尾词和购买意图'],
        ['多角度展示', '正面、侧面、背面、细节', '提升图片相关性和用户停留'],
        ['FAQ/清单', '回答生成、审核、商用和素材准备', '利于摘要抓取和转化'],
      ],
    },
    checklistTitle: 'AI Lookbook 检查清单',
    checklist: [
      '系列 Brief 和 Shot List 已完成。',
      '全量制作前已批准视觉方向。',
      '重点商品已包含所需角度和细节。',
      '每个画面的商品准确性已检查。',
      '模特、光线、背景和裁切节奏保持连贯。',
      '最终顺序讲述了清晰的系列故事。',
      '文件、alt 文本、链接和 CTA 已准备发布。',
    ],
    sourceTitle: '延伸参考',
    sources: sources('zh', ['seoStarter', 'articleSchema', 'additionalImage', 'lifestyleImage']),
    faq: [
      {
        question: 'AI Lookbook 应该包含多少张图？',
        answer: '使用足够讲清系列故事和商品的图片即可。一组较小但连贯的 Lookbook，通常比包含重复或不一致画面的大型图集更强。',
      },
      {
        question: 'AI Lookbook 可以支持电商吗？',
        answer: '可以。它可以增加 Editorial 语境和更多商品视图，但应连接到清晰商品信息，也不能错误呈现版型、结构或所含功能。',
      },
      {
        question: '怎样避免 AI Lookbook 看起来像拼图？',
        answer: '写一份系列 Brief，批准一个统一视觉方向，并在全量生成之前先规划整个序列。',
      },
    ],
    ctaTitle: '把系列方向转成连贯的 Lookbook',
    ctaBody: '规划故事，批准视觉系统，再延展成主打造型、辅助画面和商品细节。',
    ctaLabel: '进入 AI Lookbook 生成',
  },
  'image-to-video-fashion-ads': {
    slug: 'image-to-video-fashion-ads',
    path: '/guides/image-to-video-fashion-ads',
    priority: 0.71,
    landingPath: '/image-to-video-fashion',
    title: '图生视频时尚广告：从已批准静帧到短视频创意',
    metadataTitle: '图生视频时尚广告完整指南 | DOBIDOBI',
    description: '一套把选定时尚静帧转成短视频素材的实用工作流，覆盖可控运动、渠道画幅、字幕和审核。',
    primaryKeyword: '图生视频',
    searchIntent: '商业型 / 投放素材制作',
    readingTime: '约 8 分钟',
    heroImage: '/landing/seo-cases/scene-library-fashion-editorial.webp',
    heroImageAlt: '图生视频时尚短片：同一服装模特在场景库背景中生成广告视觉',
    takeawaysTitle: '核心要点',
    takeaways: [
      '添加运动之前先批准静帧。',
      '每条短片只负责一个任务：Hook、展示、演示或转化。',
      '从开始就按目标比例和界面覆盖区构图。',
      '逐帧检查商品、人物、文字和运动稳定性。',
      '把每次生成当作一个素材包，而不是单个文件。',
    ],
    sections: [
      {
        id: 'still',
        title: '为什么要先确认静帧',
        paragraphs: [
          '图生视频是在已有视觉方向上添加运动。从已批准静帧开始，团队可以在运动引入新变量之前先确认商品、模特、造型、光线和构图。',
          '如果源图含有错误 Logo、变形衣服、异常手部或无法使用的裁切，动画不会修复它。应把这张静帧当作将直接上线的素材来审核。',
        ],
      },
      {
        id: 'clip-job',
        title: 'Step 1：为短片定义一个任务',
        paragraphs: [
          '短片应只执行一个主要任务。避免只说“自然地动”而不解释目的；可控的轻微推进、小幅姿势变化、布料摆动或背景运动，比同时执行多个动作更容易审核。',
        ],
        bullets: [
          'Hook：在开场瞬间赢得注意。',
          '展示：介绍商品或完整廓形。',
          '演示：展示面料运动、角度或商品互动。',
          '氛围：延展 Campaign 气质。',
          '转化：以清晰商品或品牌行动收尾。',
        ],
        visuals: [
          guideVisual('imageToVideoSourceStill', '用于时尚图生视频的已审核白色造型源静帧', 'Step 1：只使用商品、人物、光线和构图均已审核的源静帧。'),
        ],
      },
      {
        id: 'motion-plan',
        title: 'Step 2：编写运动计划',
        paragraphs: [
          '描述主体、镜头、运动、时长和结束状态。计划应足够短，使每条指令都服务于短片的主要任务。不同工作流提供的控件可能不同，因此应把这当作规划框架，而不是所有运动都能完全锁定的承诺。',
        ],
        bullets: [
          '主体：模特保持居中，服装完整可见。',
          '镜头：缓慢推近。',
          '运动：布料和头发轻微摆动，手部保持稳定。',
          '时长：短循环。',
          '结束状态：构图回到适合放置 CTA 的干净画面。',
        ],
        visuals: [
          guideVisual('imageToVideoMotionPlanInterface', 'DOBIDOBI 图生视频工作流中的运动计划设置界面', 'Step 2：为镜头写清推进、布料运动、背景变化和停止条件。'),
        ],
      },
      {
        id: 'format',
        title: 'Step 3：生成前规划画幅',
        paragraphs: [
          '竖版、方形和横版有不同构图需求。生成前就决定目标渠道和比例，并让重要商品细节、人脸、Logo 和文字远离平台界面或字幕常覆盖的区域。',
          '不要假设竖版短片可以裁成同样强的横版素材。当多种格式都重要时，应分别制作并审核每个构图版本。',
        ],
        visuals: [
          guideVisual('imageToVideoFormatPlanningInterface', 'DOBIDOBI 图生视频比例、时长与格式规划界面', 'Step 3：在生成前确定比例、时长和安全区，避免事后裁切破坏主体。'),
        ],
      },
      {
        id: 'caption',
        title: 'Step 4：拆分创意版本',
        paragraphs: [
          '把一个已批准方向整理成素材包，使其可以用于社媒、广告和网页，而不是把一个导出文件强行塞进每个展示位。',
        ],
        bullets: [
          '用于素材库和后续编辑的干净版。',
          '用于自然社媒的轻字幕版。',
          '用于付费媒体或落地页的 CTA 版。',
          '封面图或已批准首帧。',
          '渠道专用竖版、方形或横版版本。',
        ],
        visuals: [
          guideVisual('imageToVideoAssetPackage', '使用真实广告视频整理无字版、字幕版、CTA 版、封面图和不同渠道比例的素材包', 'Step 4：从一个已审核视频整理出无字版、字幕版、CTA 版、封面图，以及 9:16、1:1、16:9 渠道版本。'),
        ],
      },
      {
        id: 'frame-review',
        title: 'Step 5：逐帧审核生成结果',
        paragraphs: [
          '检查整条短片，不要只看第一帧和最后一帧。如果商品在运动中发生实质变化，不要把该短片作为商品展示；应使用更简单的运动计划重新生成，或直接使用已批准静帧。',
        ],
        bullets: [
          '服装的形状、颜色、图案或长度是否改变？',
          '人脸、手部和身体是否稳定？',
          'Logo 或文字是否闪烁、扭曲？',
          '背景是否出现不需要的物体或文字？',
          '运动是否符合物理逻辑且观看舒适？',
          '视频是否可能误导观众理解商品？',
        ],
        visuals: [
          guideVisual('imageToVideoFrameTestGrid', '时尚图生视频逐帧测试网格用于检查人物、服装和背景稳定性', 'Step 5：逐帧对比服装、人物、手部、Logo、文字和背景是否保持稳定。'),
        ],
      },
      {
        id: 'variable-test',
        title: 'Step 6：测试正确的变量',
        paragraphs: [
          '封面测试评估第一帧、Hook 和标题；运动测试评估动作是否有助于保持注意或解释商品；CTA 测试评估结尾和下一步行动。',
          '一次只改变一个有意义的变量。在一次测试中同时比较完全不同的封面、运动、字幕和受众，会让结果无法解读。',
        ],
        visuals: [
          guideVisual('imageToVideoFashionAdStep6', '时尚图生视频广告素材包的生成结果视频', 'Step 6：把审核通过的版本整理成无字版、字幕版、CTA 版、封面图和不同渠道比例的素材包。'),
        ],
      },
    ],
    commonMistakesTitle: '需要避免的常见错误',
    commonMistakes: [
      '给未审核的静帧加动画，让原有错误进入每一帧。',
      '同时加入太多动作，增加人物、服装和背景漂移。',
      '生成完成后才裁切，导致人物、商品或字幕空间被切掉。',
      '字幕压住商品，没有预留负空间与平台界面覆盖区。',
      '没有保存源静帧、运动计划、比例、时长、版本与审核决定。',
    ],
    table: {
      title: '时尚图生视频的素材版本规划',
      headers: ['版本', '适合渠道', '制作重点'],
      rows: [
        ['无字版', '素材库、自动组合、二次剪辑', '人物和商品稳定，画面留白充足'],
        ['轻字幕版', '小红书、抖音、Instagram Reels', '第一秒 hook，字幕不挡主体'],
        ['CTA 版', '广告投放、落地页首屏', '结尾明确行动，品牌和商品露出清楚'],
        ['横版裁切', '搜索广告、官网、展示位', '主体居中，安全区保留'],
      ],
    },
    checklistTitle: '图生视频发布前检查清单',
    checklist: [
      '源静帧已批准。',
      '短片只有一个明确任务。',
      '运动可控且与任务相关。',
      '构图适合目标格式。',
      '商品、人物、Logo、文字和背景保持稳定。',
      '干净版、字幕版、CTA 版和封面版已整理。',
      '已按目标渠道审核宣传声明和合成媒体要求。',
    ],
    sourceTitle: '延伸参考',
    sources: sources('zh', ['adsImage', 'seoStarter', 'articleSchema']),
    faq: [
      {
        question: '图生视频适合做正式广告吗？',
        answer: '可以，但已批准静帧和完整短片都必须审核商品准确性、运动稳定性、宣传声明和渠道要求。',
      },
      {
        question: '为什么不要直接文字生成视频？',
        answer: '文生视频适合概念探索，但已批准静帧会为品牌商品工作提供更清晰的视觉锚点和更可审核的起点。',
      },
      {
        question: '一张静帧可以生成多个版本吗？',
        answer: '可以。使用同一已批准方向生成干净版、字幕版、CTA 版和渠道专用版本，并独立审核每个输出。',
      },
    ],
    ctaTitle: '把已批准时尚静帧转成短视频创意',
    ctaBody: '选择最强画面，定义短片任务，规划运动与格式，再逐帧审核结果。',
    ctaLabel: '进入图生视频时尚短片',
  },
};

const enGuides: Record<SeoGuideSlug, SeoGuideArticle> = {
  'product-photography-ideas': buildProductIdeasGuide('en'),
  'ai-fashion-model-generator-ecommerce': {
    ...zhGuides['ai-fashion-model-generator-ecommerce'],
    title: 'How to Use an AI Fashion Model Generator for Ecommerce Launches',
    metadataTitle: 'AI Fashion Model Generator for Ecommerce Guide | DOBIDOBI',
    description: 'A practical workflow for turning a flat-lay or garment photo into consistent on-model visuals for product pages, campaigns, and social content.',
    primaryKeyword: 'AI fashion model images',
    searchIntent: 'Commercial / pre-purchase evaluation',
    readingTime: '8 min read',
    heroImageAlt: 'AI fashion model generator example combining a model library pose with product imagery',
    heroSlides: [
      { src: '/guides/ai-fashion-model/hero-slides/01-satin-dress.webp', alt: 'Full-body fashion model wearing a champagne satin dress', width: 2048, height: 2731 },
      { src: '/guides/ai-fashion-model/hero-slides/02-black-tailoring.jpeg', alt: 'Fashion model wearing a black blazer and sequined top', width: 896, height: 1200 },
      { src: '/guides/ai-fashion-model/hero-slides/03-beige-jacket.jpeg', alt: 'Fashion model wearing a beige cropped jacket, blue knit top, and shorts', width: 896, height: 1200 },
      { src: '/guides/ai-fashion-model/hero-slides/04-black-bag-look.webp', alt: 'Fashion model wearing a black top and white skirt with a black handbag', width: 2048, height: 2743 },
      { src: '/guides/ai-fashion-model/hero-slides/05-black-romper.webp', alt: 'Fashion model wearing a black short-sleeve romper and heels', width: 1024, height: 1536 },
      { src: '/guides/ai-fashion-model/hero-slides/06-white-jumpsuit.png', alt: 'Fashion model wearing a white sleeveless jumpsuit in an indoor setting', width: 1024, height: 1376 },
    ],
    takeawaysTitle: 'Key takeaways',
    takeaways: [
      'Start with a clear garment image and define what must not change.',
      'Choose the model, pose, setting, and channel before generating.',
      'Review garment accuracy before judging the overall aesthetic.',
      'Build a coordinated launch set from one approved direction.',
      'Use AI output as a reviewed production asset, not an automatic final image.',
    ],
    sections: [
      {
        id: 'intent',
        title: 'What an AI fashion model generator should help you do',
        paragraphs: [
          'An AI fashion model generator places a real garment or product onto a generated model without requiring a new photo shoot for every concept. The most useful workflow is not “generate a model.” It is “turn one approved garment into a coordinated set of on-model images for specific ecommerce and campaign needs.”',
          'That distinction matters. A visually impressive image can still be unusable if the garment color shifts, the neckline changes, a logo disappears, or the model pose hides the product. Start by defining the business goal and the product details that must remain accurate.',
        ],
      },
      {
        id: 'materials',
        title: 'Step 1: prepare the garment image',
        paragraphs: [
          'Use a clean, well-lit image with the full garment visible. Avoid heavy shadows, folds that hide construction, hands covering the product, or low-resolution crops. For patterned garments, logos, hardware, and unusual seams, add close-up reference images when the workflow allows it.',
          'Before generation, record the non-negotiable details: color, print placement, length, silhouette, neckline, sleeves, closures, and branding. This becomes the review checklist for every result.',
        ],
        visuals: [
          guideVisual('fashionModelGarmentSource', 'Front product image of a beige trench coat prepared for AI fashion model generation', 'Step 1: use an unobstructed product image with clear garment structure to lock the product truth.'),
        ],
      },
      {
        id: 'workflow',
        title: 'Step 2: define the model and launch context',
        paragraphs: [
          'Choose the model based on the customer and the campaign, not only on what looks fashionable. Consider age range, presentation, body type, pose, and the level of diversity needed across the set.',
          'Then define where the image will be used. A product-detail page needs a clear view of fit and silhouette. A paid-social image may need stronger emotion and negative space for copy. A lookbook should maintain a consistent visual rhythm across multiple products.',
        ],
        visuals: [
          guideVisual('fashionModelReference', 'Female model reference selected for the AI fashion model workflow', 'Step 2: select one model and pose reference to control mood, composition, and lighting.'),
        ],
      },
      {
        id: 'qa',
        title: 'Step 3: generate directions before producing volume',
        paragraphs: [
          'Begin with a small set of directions that vary one decision at a time, such as pose or background. Do not generate dozens of final assets before confirming that the garment, model, and visual direction work together.',
          'Select one direction based on product accuracy, brand fit, and channel usefulness. Once approved, extend it into the required angles and formats.',
        ],
        visuals: [
          guideVisual('fashionModelGenerationInputs', 'DOBIDOBI editor combining a model reference and trench coat product image for generation', 'Step 3: combine the model reference and garment image in DOBIDOBI before generating the first direction.'),
        ],
      },
      {
        id: 'conversion',
        title: 'Step 4: review the garment before the model',
        paragraphs: [
          'Check the product in a fixed order. If a material product detail is wrong, treat the image as a concept, not a publishable ecommerce asset. Human review is especially important when the image could affect a shopper’s understanding of fit, construction, or included features.',
        ],
        bullets: [
          'Color and pattern.',
          'Silhouette and proportion.',
          'Neckline, sleeves, seams, and closures.',
          'Logo, text, hardware, and accessories.',
          'Natural fit and interaction with the body.',
          'Hands, face, anatomy, and background artifacts.',
        ],
        visuals: [
          guideVisual('fashionModelReviewResults', 'DOBIDOBI result review interface for checking trench coat color, proportion, and details', 'Step 4: verify garment color, structure, and details before evaluating the model and overall mood.'),
        ],
      },
      {
        id: 'dobi',
        title: 'Step 5: build a coordinated launch set',
        paragraphs: [
          'Keep the garment source and selected visual reference consistent while adapting the composition for each channel. This creates continuity without forcing the same crop to do every job.',
        ],
        bullets: [
          'Clear front-facing on-model image.',
          'Side or back view when relevant.',
          'Detail crop showing fabric or construction.',
          'Lifestyle frame for social content.',
          'Ad variation with clean space for copy.',
          'Approved still that can anchor a short video.',
        ],
        visuals: [
          guideVisual('fashionModelApprovedLaunchImage', 'Approved full-body AI ecommerce model image featuring the beige trench coat', 'Step 5: add the approved image to a coordinated ecommerce launch set.'),
        ],
      },
    ],
    commonMistakesTitle: 'Common mistakes to avoid',
    commonMistakes: [
      'Generating before defining the use case. The right image depends on whether it is for a listing, lookbook, ad, or social post.',
      'Approving the mood before the garment. Attractive lighting cannot compensate for a changed product.',
      'Changing too many variables at once. It becomes difficult to learn whether the model, pose, background, or reference caused the improvement.',
      'Publishing without disclosure or review. Follow the policies and labeling requirements that apply to the market, platform, and use of synthetic media.',
    ],
    table: {
      title: 'How AI model images fit the ecommerce workflow',
      headers: ['Stage', 'What AI helps with', 'What humans must review'],
      rows: [
        ['Design review', 'Preview fit, styling, and model mood', 'Garment structure and silhouette accuracy'],
        ['Launch preview', 'Create social covers and campaign directions', 'Brand tone, rights, and trademark safety'],
        ['Product page', 'Add lifestyle and angle variations', 'Color, material, sizing, and platform rules'],
        ['Ad testing', 'Expand scenes, ratios, and moods', 'Safe areas, claims, and disclosure requirements'],
      ],
    },
    checklistTitle: 'AI fashion model image checklist',
    checklist: [
      'The garment matches the source image.',
      'The model and pose fit the target customer and channel.',
      'The product remains visible and commercially understandable.',
      'Hands, anatomy, text, logos, and backgrounds have been reviewed.',
      'Each output has a defined placement and ratio.',
      'Final files include descriptive filenames and alt text.',
    ],
    sourceTitle: 'References',
    sources: sources('en', ['imageLink', 'additionalImage', 'lifestyleImage', 'adsImage']),
    faq: [
      {
        question: 'Can AI fashion models replace traditional shoots?',
        answer: 'They can reduce the cost and time required for early direction testing and asset expansion. Traditional photography may still be preferable when exact fit, movement, texture, talent rights, or high-stakes campaign production requires it.',
      },
      {
        question: 'Can I turn a flat-lay image into an on-model photo?',
        answer: 'Yes. A clear flat lay is a strong starting point, especially when the garment’s silhouette and details are visible. Complex draping, transparent materials, and hidden construction require closer review.',
      },
      {
        question: 'How do I keep the same style across multiple products?',
        answer: 'Approve one model and visual direction first, then reuse the same reference, lighting logic, background, and crop system. Change the product while keeping the campaign rules stable.',
      },
    ],
    ctaTitle: 'Create your first on-model launch set',
    ctaBody: 'Start with one clear garment image, approve the direction, and expand it into the angles and formats your launch actually needs.',
    ctaLabel: 'Open AI fashion model generator',
  },
  'ai-product-photography-workflow': buildProductPhotographyGuide('en'),
  'reference-image-ai-generation-workflow': {
    ...zhGuides['reference-image-ai-generation-workflow'],
    title: 'Reference Image AI Generation Workflow',
    description: 'How to use visual references to control composition, lighting, mood, and campaign consistency without copying another brand’s work.',
    primaryKeyword: 'reference image generation',
    searchIntent: 'Informational / commercial',
    readingTime: '8 min read',
    heroImageAlt: 'Reference image AI generation example extending a neutral resort fashion reference into lookbook visuals',
    takeawaysTitle: 'Key takeaways',
    takeaways: [
      'Separate product truth from visual direction.',
      'Describe what you want to borrow from a reference, not merely what is pictured.',
      'Lock a direction before generating a full asset set.',
      'Review for brand fit, product accuracy, and unwanted imitation.',
      'Save approved references as a reusable visual system.',
    ],
    sections: [
      {
        id: 'why-reference',
        title: 'Why reference images are useful',
        paragraphs: [
          'Text prompts can describe a mood, but visual references communicate relationships that are difficult to express precisely: camera distance, light softness, background density, color balance, negative space, and styling rhythm.',
          'A reference should guide the visual system—not replace the product source. Use a clear product image to preserve the item and a separate reference to shape the scene. This separation makes the workflow easier to review and repeat.',
        ],
      },
      {
        id: 'select',
        title: 'Step 1: identify the job of each input',
        paragraphs: [
          'Not every project needs four images, but every input should have one clear job. When one image is expected to control everything, the output often drifts.',
        ],
        bullets: [
          'Product source: defines the item that must remain accurate.',
          'Scene reference: guides environment, composition, camera angle, and lighting.',
          'Brand reference: guides palette, visual restraint, styling, and overall tone.',
          'Channel reference: shows the practical crop, safe area, or layout required for the final placement.',
        ],
        visuals: [
          guideVisual('referenceProductSource', 'Brown jacket product source used in the reference-image workflow', 'Step 1: the product source locks the jacket color, structure, and material.'),
          guideVisual('referenceSceneReference', 'Neutral beige studio scene reference used in the reference-image workflow', 'Step 1: the scene reference controls only the background, light, and spatial mood.'),
          guideVisual('referenceBrandChannelReference', 'Dark long-coat editorial reference used for brand composition and channel direction', 'Step 1: the brand and channel reference controls composition rhythm and visual attitude.'),
        ],
      },
      {
        id: 'decompose',
        title: 'Step 2: translate the reference into visual decisions',
        paragraphs: [
          'Before generating, write down the transferable qualities of the reference. This exercise helps the team agree on direction and reduces accidental imitation. The goal is to recreate visual principles in the brand’s own system, not to duplicate a specific image.',
        ],
        bullets: [
          'Composition: centered, asymmetrical, close crop, or full body.',
          'Camera: eye level, low angle, overhead, or macro.',
          'Lighting: soft daylight, hard flash, rim light, or studio gradient.',
          'Palette: warm neutral, high contrast, muted, or monochrome.',
          'Background: minimal, editorial, environmental, or textured.',
          'Energy: quiet, playful, premium, technical, or nostalgic.',
          'Negative space: location reserved for copy or interface elements.',
        ],
        visuals: [
          guideVisual('referenceImageDecompositionInterface', 'DOBIDOBI interface breaking a reference image into composition, lens, light, color palette, and background variables', 'Step 2: translate the reference into actionable composition, lens, light, palette, background, and negative-space decisions.'),
        ],
      },
      {
        id: 'series',
        title: 'Step 3: test one variable at a time',
        paragraphs: [
          'Generate a small comparison set. Keep the product fixed and test two or three scene or lighting options. After selecting the strongest direction, keep that reference stable while expanding into new products, angles, or formats.',
          'If every generation changes the product, model, scene, lighting, and crop simultaneously, the team cannot tell which decision improved the result.',
        ],
        visuals: [
          guideVisual('referenceComparison1', 'First brown jacket comparison generated under a controlled reference direction', 'Step 3: keep the product and primary reference fixed while changing one visual variable.'),
          guideVisual('referenceComparison2', 'Second brown jacket comparison generated under a controlled reference direction', 'Step 3: compare pose changes while checking jacket structure and series consistency.'),
          guideVisual('referenceComparison3', 'Third brown jacket comparison generated under a controlled reference direction', 'Step 3: compare composition changes while preserving product color and material.'),
          guideVisual('referenceComparison4', 'Fourth brown jacket comparison generated under a controlled reference direction', 'Step 3: retain the approved direction as one series instead of restarting randomly.'),
        ],
      },
      {
        id: 'review-reference',
        title: 'Step 4: review reference influence',
        paragraphs: [
          'Reject outputs that reproduce distinctive logos, text, identifiable characters, or highly specific creative elements that should not be carried over. Teams should use references they own, license, or are otherwise permitted to use and should review the resulting assets before commercial publication.',
        ],
        bullets: [
          'Is the product still accurate?',
          'Does the output reflect the intended visual qualities?',
          'Does it feel like the brand rather than a copy of the source?',
          'Can the direction work across a full campaign set?',
        ],
        visuals: [
          guideVisual('referenceReviewInterface', 'DOBIDOBI result review interface for checking how references influence generated output', 'Step 4: review whether the reference changed product information that should remain fixed.'),
        ],
      },
      {
        id: 'save-system',
        title: 'Step 5: turn the approved reference into a system',
        paragraphs: [
          'Save the reference alongside a short direction note. Record the approved palette, lighting, camera logic, background density, and channel crops. Reuse that package when generating the next product or campaign asset.',
          'A reusable direction can support product photography, fashion model images, a lookbook, ad variations, and an approved still for image-to-video. The value comes from consistency across the set—not from a single surprising image.',
        ],
        visuals: [
          guideVisual('referenceComparison1', 'Approved brown jacket output from the reference-image workflow', 'Step 5: save the approved output, inputs, and review notes as a reusable visual system.'),
        ],
      },
    ],
    commonMistakesTitle: 'Common mistakes to avoid',
    commonMistakes: [
      'Using a copyrighted campaign as a copying instruction instead of extracting general visual qualities.',
      'Combining references that give conflicting scene, light, and styling directions.',
      'Relying on vague labels such as “luxury” or “cinematic” without describing light, palette, framing, and texture.',
      'Changing the reference after every image instead of creating a coherent set.',
    ],
    table: {
      title: 'Roles of references in an AI workflow',
      headers: ['Reference type', 'Controls', 'Should not control'],
      rows: [
        ['Main style reference', 'Light, color, composition, mood', 'Exact product details'],
        ['Product reference', 'Shape, material, proportion, close-up details', 'Scene mood'],
        ['Channel reference', 'Ratio, layout, safe area', 'Brand taste'],
        ['Case reference', 'Team alignment and comparison', 'Copying third-party creative'],
      ],
    },
    checklistTitle: 'Reference workflow checklist',
    checklist: [
      'Every input has one defined role.',
      'The product source is clear and accurate.',
      'The reference has been translated into specific visual decisions.',
      'The output is original and appropriate for commercial review.',
      'The approved direction can scale across products and channels.',
      'Product details, text, logos, hands, and backgrounds have been checked.',
    ],
    sourceTitle: 'References',
    sources: sources('en', ['seoStarter', 'articleSchema', 'adsImage']),
    faq: [
      {
        question: 'What makes a good reference image?',
        answer: 'A useful reference clearly communicates the visual quality you want to control, such as composition, light, palette, or setting. It should be relevant to the final channel and legally appropriate for your use.',
      },
      {
        question: 'Can I use more than one reference?',
        answer: 'Yes, when each reference has a distinct job. Avoid multiple images that give conflicting directions.',
      },
      {
        question: 'How do references improve consistency?',
        answer: 'They give each generation the same visual anchor. Combined with a fixed product source and written direction, this reduces random changes across a campaign set.',
      },
    ],
    ctaTitle: 'Build a repeatable visual direction',
    ctaBody: 'Start with a clear product image, define what the reference controls, approve one direction, and reuse it across the full asset set.',
    ctaLabel: 'Open reference image generator',
  },
  'ai-lookbook-production-guide': {
    ...zhGuides['ai-lookbook-production-guide'],
    title: 'AI Lookbook Production Guide',
    description: 'A practical workflow for turning a collection brief, product images, and visual references into a coherent digital lookbook.',
    primaryKeyword: 'AI lookbook',
    searchIntent: 'Commercial / content production',
    readingTime: '9 min read',
    heroImageAlt: 'AI lookbook generation example extending fashion sketches into a connected model series',
    takeawaysTitle: 'Key takeaways',
    takeaways: [
      'Plan the collection story before generating individual looks.',
      'Define a repeatable shot list and visual system.',
      'Keep model mood, lighting, background, and crop rhythm consistent.',
      'Review product accuracy and continuity across the whole sequence.',
      'Design the lookbook around the reader’s decision journey.',
    ],
    sections: [
      {
        id: 'series',
        title: 'What makes a lookbook feel like one collection',
        paragraphs: [
          'A lookbook is not simply a folder of attractive fashion images. It is a sequence that introduces a collection, establishes its visual language, shows the most important products, and gives enough detail for the viewer to understand the pieces.',
          'AI can help test directions and produce variations, but the sequence still needs editorial planning. Without a collection brief, repeated model logic, and a shot list, the result often looks like unrelated images assembled after the fact.',
        ],
      },
      {
        id: 'inputs',
        title: 'Step 1: write the collection brief',
        paragraphs: [
          'Define the audience, season, product focus, and visual mood in a short paragraph. Then list what must remain consistent. This brief becomes the standard for selecting references and reviewing outputs.',
        ],
        bullets: [
          'Model or model family.',
          'Lighting and color palette.',
          'Background density.',
          'Camera distance and angle.',
          'Image ratio and crop rhythm.',
          'Styling rules.',
          'Product details that cannot change.',
        ],
        visuals: [
          guideVisual('lookbookCollectionBrief', 'AI lookbook collection brief and visual moodboard example', 'Step 1: define the season, audience, palette, scene, and forbidden elements in a collection brief.'),
        ],
      },
      {
        id: 'angles',
        title: 'Step 2: plan the sequence',
        paragraphs: [
          'A simple digital lookbook can follow the structure below. Plan the order before generating volume. Knowing the role of each page prevents unnecessary images and makes the set easier to review.',
        ],
        bullets: [
          'Collection opener: establishes the season, audience, and mood.',
          'Hero looks: leads with the strongest silhouettes or most important products.',
          'Supporting looks: expands the collection without repeating the same pose or crop.',
          'Multi-angle and detail views: shows construction, fabric, styling, and fit where relevant.',
          'Closing frame: reinforces the collection identity and leads to the next action.',
        ],
        visuals: [
          guideVisual('lookbookSequencePlanning', 'DOBIDOBI interface for planning AI lookbook page order and visual rhythm', 'Step 2: plan the opener, hero pieces, details, styling, and closing sequence before generation.'),
        ],
      },
      {
        id: 'shot-list',
        title: 'Step 3: build the shot list',
        paragraphs: [
          'For each priority product, decide whether you need front, side, back, close detail, seated, movement, or environmental frames. Not every product needs every angle.',
          'Keep the shot logic consistent across comparable items. For example, if every jacket has a front hero and detail crop, shoppers can compare them more easily. Reserve more experimental scenes for collection openers and campaign frames.',
        ],
        visuals: [
          guideVisual('lookbookShotList', 'AI lookbook shot list covering front, side, back, and detail angles', 'Step 3: build a shot list that covers front, side, back, and product details.'),
        ],
      },
      {
        id: 'visual-direction',
        title: 'Step 4: lock one visual direction',
        paragraphs: [
          'Select one approved reference direction before producing the full collection. Keep lighting, palette, model mood, and background rules stable. Introduce variation through pose, styling, and page role—not random changes to the entire visual system.',
          'When different models are used, maintain continuity through light, framing, styling, and color treatment. Diversity and consistency can coexist when the editorial rules remain clear.',
        ],
        visuals: [
          guideVisual('lookbookVisualDirection', 'Locked visual direction and moodboard for an AI lookbook series', 'Step 4: lock one lighting, palette, scene, and model direction for the series.'),
        ],
      },
      {
        id: 'review-sequence',
        title: 'Step 5: review the sequence, not only each image',
        paragraphs: [
          'First review each product for color, silhouette, pattern, logos, closures, and construction. Then review the lookbook as a whole. A strong individual image may still be removed if it disrupts the collection rhythm.',
        ],
        bullets: [
          'Does the opening establish a clear direction?',
          'Do adjacent pages feel intentionally related?',
          'Are poses and crops varied without becoming inconsistent?',
          'Are hero products easy to identify?',
          'Do detail views add useful information?',
          'Does the final sequence lead naturally to products or the campaign CTA?',
        ],
        visuals: [
          guideVisual('lookbookSequenceReview', 'DOBIDOBI interface for reviewing AI lookbook sequence and consistency', 'Step 5: review model identity, light, garment color, and crop rhythm across the whole sequence.'),
        ],
      },
      {
        id: 'prepare-publishing',
        title: 'Step 6: prepare the digital experience',
        paragraphs: [
          'Optimize images for fast loading while preserving enough resolution for the intended display. Use descriptive filenames and alt text that explain the product and scene. Link products or relevant collection pages where the format allows it.',
          'If the lookbook is published as a webpage, include a clear title, short introduction, product paths, and a next action. A lookbook can inspire, but it should also help the viewer continue toward discovery or purchase.',
        ],
        visuals: [
          guideVisual('lookbookDigitalExperience', 'Approved full-body AI lookbook image prepared for digital publishing', 'Step 6: prepare each approved image for its page, social, or advertising role.'),
        ],
      },
    ],
    commonMistakesTitle: 'Common mistakes to avoid',
    commonMistakes: [
      'Generating every look from a new prompt and ending with a collage instead of a collection.',
      'Using one pose and crop repeatedly, confusing consistency with duplication.',
      'Hiding product details behind mood and making the item commercially unclear.',
      'Skipping the sequence review even though page order changes how the collection is understood.',
    ],
    table: {
      title: 'Recommended AI lookbook page structure',
      headers: ['Module', 'Content focus', 'SEO role'],
      rows: [
        ['Collection opener', 'Theme, season, audience', 'Align H1 and first paragraph with the main keyword'],
        ['Hero pieces', 'Silhouette, fabric, styling', 'Cover long-tail and purchase intent'],
        ['Multi-angle section', 'Front, side, back, detail', 'Improve image relevance and dwell time'],
        ['FAQ/checklist', 'Generation, review, commercial use', 'Support answer extraction and conversion'],
      ],
    },
    checklistTitle: 'AI lookbook checklist',
    checklist: [
      'The collection brief and shot list are complete.',
      'The visual direction is approved before full production.',
      'Priority products have the necessary angles and details.',
      'Product accuracy has been checked in every frame.',
      'Model, light, background, and crop rhythm remain coherent.',
      'The final order tells a clear collection story.',
      'Files, alt text, links, and CTA are ready for publication.',
    ],
    sourceTitle: 'References',
    sources: sources('en', ['seoStarter', 'articleSchema', 'additionalImage', 'lifestyleImage']),
    faq: [
      {
        question: 'How many images should an AI lookbook include?',
        answer: 'Use enough images to tell the collection story and show the products clearly. A smaller, coherent lookbook is usually stronger than a large set with repeated or inconsistent frames.',
      },
      {
        question: 'Can an AI lookbook support ecommerce?',
        answer: 'Yes. It can add editorial context and additional product views, but it should connect to clear product information and should not misrepresent fit, construction, or included features.',
      },
      {
        question: 'How do you avoid a collage-like look?',
        answer: 'Write one collection brief, approve a single visual direction, and plan the sequence before generating the full set.',
      },
    ],
    ctaTitle: 'Turn a collection direction into a coherent lookbook',
    ctaBody: 'Plan the story, approve the visual system, and extend it into hero looks, supporting frames, and product details.',
    ctaLabel: 'Open AI lookbook generator',
  },
  'image-to-video-fashion-ads': {
    ...zhGuides['image-to-video-fashion-ads'],
    title: 'Image-to-Video Fashion Ads: From Approved Still to Short-Form Creative',
    metadataTitle: 'Image-to-Video Fashion Ads: Complete Guide | DOBIDOBI',
    description: 'A practical workflow for turning a selected fashion still into short video assets with controlled motion, channel-specific framing, captions, and review.',
    primaryKeyword: 'image-to-video fashion',
    searchIntent: 'Commercial / ad asset production',
    readingTime: '8 min read',
    heroImageAlt: 'Image-to-video fashion workflow example using a generated editorial still as the video anchor',
    takeawaysTitle: 'Key takeaways',
    takeaways: [
      'Approve the still image before adding motion.',
      'Give each clip one job: hook, reveal, demonstrate, or convert.',
      'Compose for the target ratio and interface overlays from the start.',
      'Review every frame for product, person, text, and motion stability.',
      'Treat each generation as an asset package, not a single file.',
    ],
    sections: [
      {
        id: 'still',
        title: 'Why the still image comes first',
        paragraphs: [
          'Image-to-video adds motion to an existing visual direction. Starting from an approved still lets the team confirm the product, model, styling, light, and composition before motion introduces new variables.',
          'If the source image contains an incorrect logo, changed garment, broken hand, or unusable crop, animation will not solve it. Approve the still as if it were going live on its own.',
        ],
      },
      {
        id: 'clip-job',
        title: 'Step 1: define one job for the clip',
        paragraphs: [
          'A short clip should do one primary job. Avoid asking the image to “move naturally” without describing the purpose. A controlled push-in, small pose shift, fabric movement, or background motion is easier to review than multiple simultaneous actions.',
        ],
        bullets: [
          'Hook: earn attention in the opening moment.',
          'Reveal: introduce the product or full silhouette.',
          'Demonstrate: show fabric movement, an angle, or a product interaction.',
          'Mood: extend the campaign atmosphere.',
          'Convert: finish with a clear product or brand action.',
        ],
        visuals: [
          guideVisual('imageToVideoSourceStill', 'Approved fashion source still with a white outfit prepared for image-to-video', 'Step 1: begin only with a still whose product, person, light, and composition have been approved.'),
        ],
      },
      {
        id: 'motion-plan',
        title: 'Step 2: write the motion plan',
        paragraphs: [
          'Describe the subject, camera, movement, duration, and ending state. Keep the plan short enough that every instruction supports the clip’s job. The exact controls available may vary by workflow, so treat this as a planning framework rather than a promise that every motion can be locked perfectly.',
        ],
        bullets: [
          'Subject: model remains centered and garment stays fully visible.',
          'Camera: slow push-in.',
          'Motion: subtle fabric and hair movement; hands remain stable.',
          'Duration: short loop.',
          'End state: composition returns to a clean frame suitable for a CTA.',
        ],
        visuals: [
          guideVisual('imageToVideoMotionPlanInterface', 'DOBIDOBI image-to-video interface for defining the motion plan', 'Step 2: define camera movement, fabric motion, background change, and stop conditions.'),
        ],
      },
      {
        id: 'format',
        title: 'Step 3: plan the format before generation',
        paragraphs: [
          'Vertical, square, and landscape placements have different composition needs. Decide the intended channel and ratio before generating. Keep important product details, faces, logos, and text away from areas commonly covered by platform interfaces or captions.',
          'Do not assume a vertical clip can be cropped into an equally strong landscape asset. When multiple formats matter, produce and review versions for each composition.',
        ],
        visuals: [
          guideVisual('imageToVideoFormatPlanningInterface', 'DOBIDOBI image-to-video interface for planning ratio, duration, and output format', 'Step 3: choose ratio, duration, and safe area before generation so later crops do not damage the subject.'),
        ],
      },
      {
        id: 'caption',
        title: 'Step 4: separate creative versions',
        paragraphs: [
          'Create an asset package so one approved direction works across social, ads, and web without forcing a single exported file into every placement.',
        ],
        bullets: [
          'Clean version for the library and future editing.',
          'Light-caption version for organic social.',
          'CTA version for paid or landing-page use.',
          'Cover image or approved first frame.',
          'Channel-specific vertical, square, or landscape versions.',
        ],
        visuals: [
          guideVisual('imageToVideoAssetPackage', 'Asset package built from the real fashion ad video: clean, captions, CTA, cover, and channel-ready ratios', 'Step 4: package one approved video into clean, captioned, CTA, cover, and 9:16, 1:1, and 16:9 channel versions.'),
        ],
      },
      {
        id: 'frame-review',
        title: 'Step 5: review frame by frame',
        paragraphs: [
          'Check the entire clip—not only the first and last frames. If the product changes materially during motion, do not use the clip as a product representation. Regenerate with a simpler motion plan or use the approved still.',
        ],
        bullets: [
          'Does the garment change shape, color, pattern, or length?',
          'Do the face, hands, and body remain stable?',
          'Do logos or text flicker or distort?',
          'Does the background introduce unwanted objects or writing?',
          'Is the motion physically plausible and comfortable to watch?',
          'Could the video mislead viewers about the product?',
        ],
        visuals: [
          guideVisual('imageToVideoFrameTestGrid', 'Fashion image-to-video frame test grid used to check person, garment, and background stability', 'Step 5: compare frames to review garment, person, hands, logos, text, and background stability.'),
        ],
      },
      {
        id: 'variable-test',
        title: 'Step 6: test the right variable',
        paragraphs: [
          'A cover test evaluates the first frame, hook, and headline. A motion test evaluates whether movement helps maintain attention or communicate the product. A CTA test evaluates the ending and next action.',
          'Change one meaningful variable at a time. Comparing completely different covers, motion, captions, and audiences in one test makes the result difficult to interpret.',
        ],
        visuals: [
          guideVisual('imageToVideoFashionAdStep6', 'Fashion image-to-video ad asset package generation result video', 'Step 6: package approved versions into clean, captioned, CTA, cover, and channel-specific ratio assets.'),
        ],
      },
    ],
    commonMistakesTitle: 'Common mistakes to avoid',
    commonMistakes: [
      'Animating an unapproved still and carrying every existing error into the clip.',
      'Adding too much motion, which increases drift and makes the asset harder to review.',
      'Cropping after generation and removing the product, face, hands, or caption space.',
      'Placing captions over the product instead of reserving negative space for interface overlays.',
      'Skipping the production record for source still, motion plan, ratio, duration, version, and review decision.',
    ],
    table: {
      title: 'Version planning for fashion image-to-video assets',
      headers: ['Version', 'Best channel', 'Production focus'],
      rows: [
        ['Clean version', 'Asset library and editing', 'Stable product, clean motion, useful negative space'],
        ['Light-caption version', 'Reels, TikTok, Xiaohongshu', 'Strong hook without covering the product'],
        ['CTA version', 'Ads and landing pages', 'Clear ending, brand and product visibility'],
        ['Landscape crop', 'Search ads and website placements', 'Centered subject and safe area'],
      ],
    },
    checklistTitle: 'Image-to-video checklist',
    checklist: [
      'The source still is approved.',
      'The clip has one defined job.',
      'Motion is controlled and relevant.',
      'Composition fits the target format.',
      'Product, person, logos, text, and background are stable.',
      'Clean, caption, CTA, and cover versions are organized.',
      'Claims and synthetic-media requirements have been reviewed for the intended channel.',
    ],
    sourceTitle: 'References',
    sources: sources('en', ['adsImage', 'seoStarter', 'articleSchema']),
    faq: [
      {
        question: 'Can image-to-video be used for real fashion ads?',
        answer: 'Yes, after the still and the complete clip have been reviewed for product accuracy, motion stability, claims, and channel requirements.',
      },
      {
        question: 'Why not generate video directly from text?',
        answer: 'Text-to-video can be useful for concept exploration, but an approved still gives the team a clearer visual anchor and a more reviewable starting point for branded product work.',
      },
      {
        question: 'Can one still create multiple versions?',
        answer: 'Yes. Use the same approved direction to create clean, caption, CTA, and channel-specific versions, reviewing each output independently.',
      },
    ],
    ctaTitle: 'Turn an approved fashion still into short-form creative',
    ctaBody: 'Choose the strongest frame, define the clip’s job, plan the motion and format, and review the result frame by frame.',
    ctaLabel: 'Open image-to-video fashion workflow',
  },
};

const guideContent: Record<SeoGuideLocale, Record<SeoGuideSlug, SeoGuideArticle>> = {
  zh: zhGuides,
  en: enGuides,
};

export function resolveGuideLocale(locale?: string | null): SeoGuideLocale {
  return locale?.toLowerCase().startsWith('zh') ? 'zh' : 'en';
}

export function getSeoGuides(locale?: string | null): SeoGuideArticle[] {
  const guideLocale = resolveGuideLocale(locale);
  return SEO_GUIDE_CONFIGS.map((config) => withReferenceStyleWorkflow(guideContent[guideLocale][config.slug], guideLocale));
}

export function getSeoGuide(slug: string, locale?: string | null): SeoGuideArticle | null {
  if (!SEO_GUIDE_CONFIGS.some((config) => config.slug === slug)) {
    return null;
  }

  const guideLocale = resolveGuideLocale(locale);
  return withReferenceStyleWorkflow(guideContent[guideLocale][slug as SeoGuideSlug], guideLocale);
}
